
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useAISuggestions, useReviewAISuggestion, useContentQualityAssessments, useSearchOptimizationMetrics } from '@/hooks/useAIGovernance';
import { 
  Brain, 
  CheckCircle, 
  XCircle, 
  Clock, 
  TrendingUp,
  Search,
  BarChart3,
  Lightbulb,
  Shield
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const AIGovernanceDashboard: React.FC = () => {
  const [selectedSuggestionType, setSelectedSuggestionType] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('pending');
  const [reviewNotes, setReviewNotes] = useState<Record<string, string>>({});

  const { data: suggestions } = useAISuggestions(
    selectedSuggestionType === 'all' ? undefined : selectedSuggestionType,
    selectedStatus === 'all' ? undefined : selectedStatus
  );
  const { data: qualityAssessments } = useContentQualityAssessments();
  const { data: searchMetrics } = useSearchOptimizationMetrics();
  const { mutate: reviewSuggestion, isPending: reviewing } = useReviewAISuggestion();

  const getSuggestionTypeIcon = (type: string) => {
    switch (type) {
      case 'connection': return '🔗';
      case 'content': return '📄';
      case 'curriculum': return '📚';
      case 'quality_improvement': return '⭐';
      default: return '🤖';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-white text-black';
      case 'approved': return 'bg-white text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'implemented': return 'bg-white text-black';
      default: return 'bg-white text-black';
    }
  };

  const handleReview = (suggestionId: string, action: 'approve' | 'reject' | 'implement') => {
    reviewSuggestion({
      suggestionId,
      action,
      notes: reviewNotes[suggestionId]
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Brain className="h-6 w-6" />
        <h2 className="text-2xl font-bold">AI Governance Dashboard</h2>
      </div>

      <Tabs defaultValue="suggestions" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="suggestions">AI Suggestions</TabsTrigger>
          <TabsTrigger value="quality">Quality Assessment</TabsTrigger>
          <TabsTrigger value="search">Search Optimization</TabsTrigger>
          <TabsTrigger value="metrics">AI Metrics</TabsTrigger>
        </TabsList>

        <TabsContent value="suggestions" className="space-y-6">
          {/* Filters */}
          <Card>
            <CardHeader>
              <CardTitle>Review AI Suggestions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Suggestion Type</label>
                  <Select value={selectedSuggestionType} onValueChange={setSelectedSuggestionType}>
                    <SelectTrigger>
                      <SelectValue placeholder="All types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All types</SelectItem>
                      <SelectItem value="connection">Connections</SelectItem>
                      <SelectItem value="content">Content</SelectItem>
                      <SelectItem value="curriculum">Curriculum</SelectItem>
                      <SelectItem value="quality_improvement">Quality Improvement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Status</label>
                  <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                    <SelectTrigger>
                      <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="implemented">Implemented</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-end">
                  <div className="text-sm text-black">
                    {suggestions?.length || 0} suggestions found
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Suggestions List */}
          <div className="space-y-4">
            {suggestions && suggestions.length > 0 ? (
              suggestions.map((suggestion) => (
                <Card key={suggestion.id} className="hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{getSuggestionTypeIcon(suggestion.suggestion_type)}</span>
                        <div>
                          <h3 className="font-medium capitalize">
                            {suggestion.suggestion_type.replace('_', ' ')} Suggestion
                          </h3>
                          <p className="text-sm text-black">
                            Confidence: {Math.round(suggestion.confidence_score * 100)}%
                          </p>
                        </div>
                      </div>
                      <Badge className={getStatusColor(suggestion.status)}>
                        {suggestion.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {suggestion.reasoning && (
                      <div>
                        <label className="text-sm font-medium">AI Reasoning:</label>
                        <p className="text-sm text-black mt-1">{suggestion.reasoning}</p>
                      </div>
                    )}

                    <div className="text-sm text-black">
                      Created {formatDistanceToNow(new Date(suggestion.created_at), { addSuffix: true })}
                      {suggestion.ai_model_version && (
                        <span className="ml-2">• Model: {suggestion.ai_model_version}</span>
                      )}
                    </div>

                    {suggestion.status === 'pending' && (
                      <div className="space-y-3 pt-4 border-t">
                        <div>
                          <label className="text-sm font-medium mb-2 block">Review Notes</label>
                          <Textarea
                            placeholder="Add your review notes..."
                            value={reviewNotes[suggestion.id] || ''}
                            onChange={(e) => setReviewNotes(prev => ({
                              ...prev,
                              [suggestion.id]: e.target.value
                            }))}
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleReview(suggestion.id, 'approve')}
                            disabled={reviewing}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleReview(suggestion.id, 'implement')}
                            disabled={reviewing}
                          >
                            <Lightbulb className="h-4 w-4 mr-1" />
                            Implement
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleReview(suggestion.id, 'reject')}
                            disabled={reviewing}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card>
                <CardContent className="text-center py-8">
                  <Brain className="h-12 w-12 text-black mx-auto mb-4" />
                  <p className="text-black">No AI suggestions found</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="quality" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Content Quality Assessments</CardTitle>
            </CardHeader>
            <CardContent>
              {qualityAssessments && qualityAssessments.length > 0 ? (
                <div className="space-y-4">
                  {qualityAssessments.slice(0, 10).map((assessment: any) => (
                    <div key={assessment.id} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium">{assessment.nodes?.title || 'Unknown Content'}</h4>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{assessment.assessment_type}</Badge>
                          <Badge className={assessment.quality_score >= 80 ? 'bg-white text-green-800' : 
                            assessment.quality_score >= 60 ? 'bg-white text-black' : 'bg-red-100 text-red-800'}>
                            {assessment.quality_score}/100
                          </Badge>
                        </div>
                      </div>
                      
                      {assessment.feedback && (
                        <p className="text-sm text-black mb-2">{assessment.feedback}</p>
                      )}
                      
                      {assessment.improvement_suggestions && assessment.improvement_suggestions.length > 0 && (
                        <div>
                          <p className="text-sm font-medium mb-1">Improvement Suggestions:</p>
                          <ul className="text-sm text-black list-disc list-inside">
                            {assessment.improvement_suggestions.map((suggestion: string, index: number) => (
                              <li key={index}>{suggestion}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Shield className="h-12 w-12 text-black mx-auto mb-4" />
                  <p className="text-black">No quality assessments found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="search" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="h-5 w-5" />
                  Popular Search Terms
                </CardTitle>
              </CardHeader>
              <CardContent>
                {searchMetrics?.popularTerms && searchMetrics.popularTerms.length > 0 ? (
                  <div className="space-y-2">
                    {searchMetrics.popularTerms.map(({ term, count }, index) => (
                      <div key={term} className="flex items-center justify-between">
                        <span className="text-sm">{index + 1}. {term}</span>
                        <Badge variant="outline">{count} searches</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-black text-center py-4">No search data available</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Zero Result Queries
                </CardTitle>
              </CardHeader>
              <CardContent>
                {searchMetrics?.zeroResultQueries && searchMetrics.zeroResultQueries.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {searchMetrics.zeroResultQueries.slice(0, 10).map((query, index) => (
                      <div key={index} className="text-sm p-2 bg-red-50 rounded border">
                        "{query}"
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-black text-center py-4">No failed searches found</p>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Search Optimization Recommendations</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-white rounded-lg border border-blue-200">
                  <h4 className="font-medium text-blue-900 mb-2">Index Optimization</h4>
                  <p className="text-sm text-black">
                    Consider adding synonyms and alternative terms for popular queries with low result counts.
                  </p>
                </div>
                
                <div className="p-4 bg-white rounded-lg border border-green-200">
                  <h4 className="font-medium text-green-900 mb-2">Content Gaps</h4>
                  <p className="text-sm text-green-700">
                    Zero-result queries indicate potential content gaps that could be filled with new articles or lessons.
                  </p>
                </div>
                
                <div className="p-4 bg-white rounded-lg border border-purple-200">
                  <h4 className="font-medium text-purple-900 mb-2">AI-Suggested Improvements</h4>
                  <p className="text-sm text-black">
                    Implement AI-suggested connections to improve content discoverability and relevance.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="metrics" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total AI Suggestions</CardTitle>
                <Brain className="h-4 w-4 text-black" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{suggestions?.length || 0}</div>
                <p className="text-xs text-black">
                  {suggestions?.filter(s => s.status === 'pending').length || 0} pending review
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Search Queries</CardTitle>
                <Search className="h-4 w-4 text-black" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{searchMetrics?.totalSearches || 0}</div>
                <p className="text-xs text-black">
                  Last 30 days
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Quality Assessments</CardTitle>
                <BarChart3 className="h-4 w-4 text-black" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{qualityAssessments?.length || 0}</div>
                <p className="text-xs text-black">
                  All time
                </p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>AI System Health</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-white0 rounded-full"></div>
                    <span className="text-sm font-medium">Suggestion Engine</span>
                  </div>
                  <Badge className="bg-white text-green-800">Operational</Badge>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-white0 rounded-full"></div>
                    <span className="text-sm font-medium">Quality Assessment</span>
                  </div>
                  <Badge className="bg-white text-green-800">Operational</Badge>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-white0 rounded-full"></div>
                    <span className="text-sm font-medium">Search Analytics</span>
                  </div>
                  <Badge className="bg-white text-green-800">Operational</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AIGovernanceDashboard;
